"use client";

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '../../contexts/AuthContext';
import { useBotStatus } from '@/hooks/useBotStatus';
import StatsCounter from '@/components/home/StatsCounter';

// Shadcn UI 컴포넌트
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

// Lucide 아이콘
import {
  ArrowRight,
  MessageSquare,
  Mic2,
  Bot,
  Music,
  Image as ImageIcon,
  Users,
  ChevronDown,
  LogIn,
  Server,
  Activity,
} from 'lucide-react';

// Discord 아이콘
import { RiDiscordFill } from "react-icons/ri";
import Link from '@/components/common/Link';

export default function Home() {
  const { user, isLoading, login } = useAuth();
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const featuresRef = useRef<HTMLDivElement>(null);
  const { status: botStatus } = useBotStatus();

  // 애니메이션을 위한 스크롤 이벤트 리스너
  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.top <= window.innerHeight * 0.8;

        if (isInViewport) {
          el.classList.add('animate-active');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 초기 로드 시 확인

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 기능 섹션으로 스크롤
  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-pink-100">
      {/* 헤더 */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 shadow-sm">
        <div className="container mx-auto py-4 px-6 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center">
              <span className="text-white font-bold">K</span>
            </div>
            <span className="font-bold text-xl text-pink-700">카미봇</span>
          </div>

          <nav className="hidden md:flex space-x-6">
            <a href="#features" className="text-pink-700 hover:text-pink-500 font-medium">기능</a>
            <a href="#usage" className="text-pink-700 hover:text-pink-500 font-medium">사용법</a>
            <a href="#faq" className="text-pink-700 hover:text-pink-500 font-medium">FAQ</a>
            {/* <a href="/donate" className="text-pink-700 hover:text-pink-500 font-medium">후원하기 💝</a> */}
          </nav>

          {isLoading ? (
            <div className="w-28 h-10 bg-gray-200 animate-pulse rounded-md"></div>
          ) : user ? (
            <Button
              onClick={() => router.push('/dashboard')}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              대시보드 <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => login()}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
            >
              <RiDiscordFill className="mr-2 h-5 w-5" />
              로그인
            </Button>
          )}
        </div>
      </header>

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-20 px-4">
        <div
          className={`container mx-auto max-w-6xl transition-all duration-1000 ease-out 
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h1 className="text-5xl md:text-6xl font-bold text-pink-800 leading-tight mb-6">
                디스코드 서버를<br />더 <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">
                  풍부하게
                </span>
              </h1>

              <p className="text-xl text-pink-700 mb-8">
                복잡한 게 싫어서 디스코드 서버를 편하게 쓰려고 무료 봇인 카미봇을 만들었어요. 한번 써보고 아쉬운 점을 알려주세요!
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Button
                    onClick={() => router.push('/dashboard')}
                    size="lg"
                    className="bg-pink-600 hover:bg-pink-700 text-white text-lg px-8 py-6 rounded-xl"
                  >
                    대시보드로 이동 <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => login()}
                    size="lg"
                    className="bg-pink-600 hover:bg-pink-700 text-white text-lg px-8 py-6 rounded-xl"
                  >
                    지금 바로 시작하기 <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="lg"
                  onClick={scrollToFeatures}
                  className="bg-transparent border-2 border-pink-500 text-pink-700 hover:!text-pink-700 hover:!bg-pink-300/20 text-lg px-8 py-6 rounded-xl"
                >
                  자세히 알아보기
                </Button>
{/* 
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => router.push('/donate')}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white border-none text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  ☕ 라면 하나 사주기!
                </Button> */}
              </div>

              {/* 실시간 통계 표시 */}
              <div className="mt-8 flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-pink-500 animate-pulse" />
                  <div className="text-pink-600">
                    <span className="font-medium">현재 </span>
                    <StatsCounter
                      value={botStatus?.users || 1000000}
                      className="font-bold"
                      formatter={(val) => `${(val).toLocaleString()}+`}
                    />
                    <span className="font-medium"> 사용자가 이용 중</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Server className="h-5 w-5 text-pink-500" />
                  <div className="text-pink-600">
                    <span className="font-medium">현재 </span>
                    <StatsCounter
                      value={botStatus?.guilds || 20000}
                      className="font-bold"
                      formatter={(val) => `${val.toLocaleString()}+`}
                    />
                    <span className="font-medium"> 서버에서 활동 중</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative w-full max-w-md h-[450px] bg-gradient-to-tr from-pink-200 to-purple-200 rounded-3xl shadow-xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="relative w-full h-full flex items-center justify-center">
                    <Image
                      src="/kamibot-hero@2x.webp"
                      alt="카미봇 캐릭터"
                      width={400}
                      height={400}
                      priority
                      className="object-contain drop-shadow-2xl mb-8"
                    />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-white/30 backdrop-blur-md p-5">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center overflow-hidden">
                      <Image
                        src="/favicon-64x64.png"
                        alt="카미봇"
                        width={40}
                        height={40}
                        className="object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-black">카미봇</div>
                      <p className="text-pink-700 text-sm">안녕하세요! 무엇을 도와드릴까요?</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 구분선 */}
      <div className="container mx-auto px-4">
        <Separator className="bg-pink-200" />
      </div>

      {/* 실시간 상태 표시 섹션 */}
      {false && <section className="py-10 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 animate-on-scroll">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center justify-center p-4">
                <div className="p-3 rounded-full bg-pink-100 mb-4">
                  <Users className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-pink-800 mb-2">총 이용자</h3>
                <p className="text-3xl font-bold text-pink-700">
                  <StatsCounter
                    value={botStatus?.users || 1000000}
                    formatter={(val) => `${(val).toLocaleString()}+`}
                  />
                </p>
                <p className="text-sm text-pink-500 mt-1">실시간 업데이트</p>
              </div>

              <div className="flex flex-col items-center justify-center p-4">
                <div className="p-3 rounded-full bg-pink-100 mb-4">
                  <Server className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-pink-800 mb-2">활성 서버</h3>
                <p className="text-3xl font-bold text-pink-700">
                  <StatsCounter
                    value={botStatus?.guilds || 20000}
                    formatter={(val) => `${val.toLocaleString()}+`}
                  />
                </p>
                <p className="text-sm text-pink-500 mt-1">전세계 디스코드 서버</p>
              </div>

              <div className="flex flex-col items-center justify-center p-4">
                <div className="p-3 rounded-full bg-pink-100 mb-4">
                  <Activity className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-lg font-semibold text-pink-800 mb-2">서버 상태</h3>
                <p className="text-xl font-bold text-pink-700">
                  {botStatus?.status || "운영 중"}
                </p>
                <p className="text-sm text-pink-500 mt-1">
                  {botStatus?.cpu_usage
                    ? `CPU 사용률: ${Math.round(botStatus?.cpu_usage || 0)}%`
                    : "모든 시스템 정상 작동"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>}

      {/* 기능 섹션 */}
      <section id="features" ref={featuresRef} className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-pink-800 mb-4">카미봇의 특별한 기능</h2>
            <p className="text-xl text-pink-600 max-w-2xl mx-auto">
              디스코드 서버의 활동성과 재미를 더해줄 다양한 기능들을 살펴보세요.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "540개의 TTS 목소리",
                description: <>사람들 목소리가 다 달라야 누가 말하는 지 구분하죠..! 재미있고 귀여운 목소리를 모두 모아봤어요. <br></br>
                  <Link>/tts</Link> 명령 하나면 바로 사용할 수 있답니다.</>,
                icon: <Mic2 className="h-8 w-8 text-pink-600" />,
                delay: 0
              },
              {
                title: "AI 어시스턴트",
                description: (
                  <>
                    <Link>@카미봇</Link> 잡담방 하나 만들고 타임아웃 5초로 설정한 다음 관리자만 볼 수 있게 해줘.
                    <Link>@카미봇</Link> 우리 서버에서 대화 많은 사람 TOP 5 알려줘!
                  </>
                ),
                icon: <Bot className="h-8 w-8 text-purple-600" />,
                delay: 100
              },
              {
                title: "이모지 업스케일링",
                description: "커스텀 이모지를 스티커처럼 크게 확대해 보세요. 딥러닝 기술로 선명하게 업스케일링해 드릴게요!",
                icon: <ImageIcon className="h-8 w-8 text-blue-600" />,
                delay: 200
              },
              {
                title: "환영 메시지",
                description: "새 멤버가 서버에 들어오면 카미봇 AI가 멋진 환영 메세지와 카드를 보내줄게요!🎉🎂",
                icon: <Users className="h-8 w-8 text-green-600" />,
                delay: 300
              },
              {
                title: "채팅 번역",
                description: "실시간으로 채팅을 여러 언어로 번역해서 표시해주는 기능도 있어요, 글로벌 서버를 위한 필수 기능이죠.",
                icon: <MessageSquare className="h-8 w-8 text-yellow-600" />,
                delay: 400
              },
              {
                title: "음악 재생",
                description: "(열심히 고치는 중) 디스코드 음성 채널에서 음악을 재생할 수 있어요.",
                icon: <Music className="h-8 w-8 text-red-600" />,
                delay: 500
              }
            ].map((feature, index) => (
              <Card
                key={index}
                className="animate-on-scroll bg-white/80 backdrop-blur-sm border-none shadow-lg rounded-xl hover:shadow-xl hover:translate-y-[-5px] transition-all duration-300"
                style={{ animationDelay: `${feature.delay}ms` }}
              >
                <CardContent className="p-6 pt-3 ">
                  <div className="mb-4 p-3 rounded-full bg-pink-100 w-fit">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-pink-800 mb-2">{feature.title}</h3>
                  <p className="text-pink-700">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 사용법 섹션 */}
      <section id="usage" className="py-20 px-4 bg-pink-100/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-pink-800 mb-4">간단한 3단계로 시작하기</h2>
            <p className="text-xl text-pink-600 max-w-2xl mx-auto">
              카미봇은 아무런 설정 없이 바로 사용할 수 있어요. 하지만 원한다면 뭐든지 설정하실 수도 있죠.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "로그인",
                description: "Discord 계정으로 간편하게 로그인하세요.",
                icon: <LogIn className="h-10 w-10 text-pink-600" />,
                number: "01"
              },
              {
                title: "봇 초대",
                description: "원하는 서버에 카미봇을 초대하세요.",
                icon: <Bot className="h-10 w-10 text-pink-600" />,
                number: "02"
              },
              {
                title: "설정 완료",
                description: "원하는 기능을 켜고 바로 사용하세요.",
                icon: <ArrowRight className="h-10 w-10 text-pink-600" />,
                number: "03"
              }
            ].map((step, index) => (
              <div key={index} className="animate-on-scroll relative">
                <div className="absolute top-0 right-0 text-5xl font-bold text-pink-200">
                  {step.number}
                </div>
                <Card className="bg-white/90 backdrop-blur-sm border-none shadow-lg rounded-xl h-full">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="mb-6 p-4 rounded-full bg-pink-100 w-fit">
                      {step.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-pink-800 mb-2">{step.title}</h3>
                    <p className="text-pink-700 flex-grow">{step.description}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl animate-on-scroll">
          <Card className="bg-gradient-to-r from-pink-500 to-purple-500 border-none shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-12 flex flex-col md:flex-row items-center justify-between">
              <div className="mb-8 md:mb-0 text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">서버에 카미봇을 초대해 보세요</h2>
                <p className="text-white/90 text-xl max-w-md">
                  벌써 {((botStatus?.users || 1000000) / 10000).toFixed(1)}만 명이 카미봇을 사용하고 있어요!<br />
                </p>
              </div>

              <div className="flex flex-col sm:flex-col gap-4">
                <Button
                  onClick={() => login()}
                  size="lg"
                  className="bg-white text-pink-600 hover:bg-pink-100 text-lg px-8 py-6 rounded-xl shadow-lg"
                >
                  <RiDiscordFill className="mr-2 h-6 w-6" />
                  Discord 로그인
                </Button>

                <Button
                  size="lg"
                  className="bg-white text-pink-600 hover:bg-pink-100 text-lg px-8 py-6 rounded-xl shadow-lg"
                  onClick={() => window.open('https://discord.com/oauth2/authorize?client_id=1019061779357245521', '_blank')}
                >
                  <span className="mr-2 text-xl">➕</span>
                  봇 초대하기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ 섹션 */}
      <section id="faq" className="py-20 px-4 bg-pink-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-pink-800 mb-4">자주 묻는 질문</h2>
            <p className="text-xl text-pink-600 max-w-2xl mx-auto">
              카미봇에 대해 궁금한 점이 있으신가요?
            </p>
          </div>

          <div className="space-y-4 animate-on-scroll">
            {[
              {
                question: "카미봇은 무료인가요?",
                answer: "네! 카미봇은 완전 무료 봇이에요. 돈 벌려고 만든 게 아니라 여러분을 편하게 해 드리고 싶어서 만든 봇이에요. 1인 개발 봇이고 남는 시간에 만들고 있어요. 많이 사용해 주세요!"
              },
              {
                question: "카미봇을 초대하려면 어떻게 해야 하나요?",
                answer: <>Discord 계정으로 로그인 후 대시보드에서 '봇 초대하기' 버튼을 클릭하시면 서버에 봇을 초대할 수 있어요. 또는 그냥 <Link href="https://discord.com/oauth2/authorize?client_id=1019061779357245521">여기</Link>를 누르세요</>
              },
              {
                question: "TTS 기능은 어떻게 사용하나요?",
                answer: <>엄청 간단해요! 음성 채널에 들어가서 <Link>/tts</Link> 명령을 치면 자동으로 켜져요! 끄려면 다시 누르면 돼요. 목소리도 바꿀 수 있어요.</>
              },
              {
                question: "지원하는 언어는 무엇인가요?",
                answer: "현재 UI 언어는 한국어로만 제공되고 있어요. 하지만 TTS, AI채팅, 채팅 번역과 같은 내부 기능들은 108가지 언어로 제공되고 있어요. 대단하죠?"
              },
              {
                question: "봇 명령어는 어떻게 확인하나요?",
                answer: "서버에서 '/help' 명령어를 입력하시면 모든 명령어 목록과 사용법을 확인하실 수 있어요. 기능 하나에는 하나의 명령어만 있어서 사용하기 매우 간단해요!"
              },
              {
                question: "봇 업데이트는 어떻게 이루어지나요?",
                answer: "대부분의 업데이트는 서비스 중단 없이 수행하고 있어서. 제가 한가한 시간대에 주로 이뤄지고 있어요. 이용자가 적은 평일 오전 9시에 가장 많이 업데이트하지만 긴급한 버그가 있다면 바로 업데이트해요..! 무중단 업데이트라곤 하지만 1~2초 정도 TTS 기능이 중단될 수 있어요. 갑자기 카미봇이 잠시 멈춰도 너그러운 양해 부탁드려요 ㅠㅁㅠ 버그를 없에고 더 나은 기능이 추가되는 순간이랍니다."
              }
            ].map((faq, index) => (
              <Card key={index} className="bg-white/90 backdrop-blur-sm border-none shadow-md rounded-xl overflow-hidden">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-pink-800 mb-2">{faq.question}</h3>
                  <p className="text-pink-700">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="py-12 px-4 bg-pink-800 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                  <span className="text-pink-800 font-bold">K</span>
                </div>
                <span className="font-bold text-xl">카미봇</span>
              </div>
              <p className="text-pink-200">
                디스코드 서버를 더 풍부하게 만들어주는 다목적 봇
              </p>
            </div>

            <div>
              <h3 className="font-bold text-lg mb-4">링크</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-pink-200 hover:text-white transition-colors">기능</a></li>
                <li><a href="#usage" className="text-pink-200 hover:text-white transition-colors">사용법</a></li>
                <li><a href="#faq" className="text-pink-200 hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>

            {/* TODO(GDPR): Kamilake OÜ 등록 완료 후 아래 블록을 활성화 (회사명/등록번호/주소/이메일은 app/privacy 및 app/terms 페이지에서 관리)
            <div>
              <h3 className="font-bold text-lg mb-4">법적 정보</h3>
              <ul className="space-y-2">
                <li><a href="/terms" className="text-pink-200 hover:text-white transition-colors">이용약관</a></li>
                <li><a href="/privacy" className="text-pink-200 hover:text-white transition-colors">개인정보처리방침</a></li>
              </ul>
            </div>
            */}

            <div>
              <h3 className="font-bold text-lg mb-4">소셜 미디어</h3>
              <div className="flex space-x-4">
                <a href="#" className="text-pink-200 hover:text-white transition-colors">
                  <RiDiscordFill className="h-6 w-6" />
                </a>
                <a href="#" className="text-pink-200 hover:text-white transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.1 10.1 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z"></path>
                  </svg>
                </a>
                <a href="#" className="text-pink-200 hover:text-white transition-colors">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.82 18.17c-.108.268-.362.377-.63.269-1.73-.859-3.91-1.055-6.48-.579-.277.053-.54-.126-.592-.403-.053-.277.126-.54.403-.592 2.813-.512 5.24-.277 7.163.689.268.108.377.362.269.63zM17.066 14.775c-.135.336-.476.462-.812.326-1.989-.989-5.016-1.27-7.367-.694-.387.101-.775-.138-.875-.525-.1-.387.138-.774.526-.875 2.69-.671 6.03-.348 8.375.813.337.135.463.476.327.812l-.174.143zm.1-2.836c-2.39-1.42-6.325-1.55-8.6-.858-.463.126-.95-.146-1.076-.607-.126-.462.147-.95.608-1.076 2.626-.793 6.99-.642 9.76 1.007.366.217.486.692.27 1.058-.218.366-.693.486-1.059.27l.097.206z"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* TODO(GDPR): Kamilake OÜ 등록 완료 후 활성화 — 정식 법인명으로 카피라이트 표기
          <div className="mt-12 pt-8 border-t border-pink-700 text-center text-pink-300">
            <p>&copy; {new Date().getFullYear()} Kamilake OÜ. All rights reserved.</p>
          </div>
          */}
        </div>
      </footer>

      {/* 애니메이션 스타일 */}
      <style jsx global>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .animate-active {
          opacity: 1;
          transform: translateY(0);
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
